import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, AlertTriangle, Utensils, Heart, CheckCircle2, XCircle,
  Download, RefreshCw, Search, Printer, Mail, MessageCircle, Camera,
  Phone, ChevronRight, Sparkles,
} from "lucide-react";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ORDER = ["4-7", "8-11", "12-15", "other"];
const GROUP_META = {
  "4-7":   { label: "Ages 4–7",  title: "Little Makers",  color: "#E91E80", bg: "rgba(233,30,128,0.08)", border: "rgba(233,30,128,0.30)" },
  "8-11":  { label: "Ages 8–11", title: "Junior Makers",  color: "#F2762E", bg: "rgba(242,118,46,0.08)", border: "rgba(242,118,46,0.30)" },
  "12-15": { label: "Ages 12–15",title: "Senior Makers",  color: "#0FA9A0", bg: "rgba(15,169,160,0.08)", border: "rgba(15,169,160,0.30)" },
  "other": { label: "Unassigned",title: "No Age Group",   color: "#52525B", bg: "rgba(82,82,91,0.08)",   border: "rgba(82,82,91,0.30)"   },
};

const hasFlag = (v) => v && v.trim() && !["none", "n/a", "na"].includes(v.trim().toLowerCase());

const AdminMarketCampGroups = () => {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("4-7");
  const [search, setSearch] = useState("");
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState("paid");

  const loadAll = async (filter) => {
    try {
      const [g, w] = await Promise.all([
        axios.get(`${API}/admin/market-camp-groups`, { params: { payment_status: filter } }),
        axios.get(`${API}/admin/whatsapp-config-status`).catch(() => ({ data: { twilio_configured: false } })),
      ]);
      return { groups: g.data, waOn: !!w.data?.twilio_configured };
    } catch {
      toast.error("Failed to load groups");
      return null;
    }
  };

  const refresh = async () => {
    setLoading(true);
    const d = await loadAll(paymentFilter);
    if (d) { setGroups(d.groups); setWhatsappConfigured(d.waOn); }
    setLoading(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const d = await loadAll(paymentFilter);
      if (active && d) { setGroups(d.groups); setWhatsappConfigured(d.waOn); setLoading(false); }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentFilter]);

  const totals = useMemo(() => {
    if (!groups) return { children: 0, allergy: 0, medical: 0, dietary: 0, checkedIn: 0 };
    return Object.values(groups).reduce((acc, g) => ({
      children: acc.children + g.count,
      allergy: acc.allergy + g.allergy_count,
      medical: acc.medical + g.medical_count,
      dietary: acc.dietary + g.dietary_count,
      checkedIn: acc.checkedIn + g.checked_in_count,
    }), { children: 0, allergy: 0, medical: 0, dietary: 0, checkedIn: 0 });
  }, [groups]);

  const filteredChildren = useMemo(() => {
    if (!groups) return [];
    const list = groups[activeTab]?.children || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(c =>
      c.full_name?.toLowerCase().includes(q) ||
      c.parent_full_name?.toLowerCase().includes(q) ||
      c.parent_email?.toLowerCase().includes(q) ||
      c.parent_phone?.includes(q) ||
      c.ticket_code?.toLowerCase().includes(q) ||
      c.registration_code?.toLowerCase().includes(q)
    );
  }, [groups, activeTab, search]);

  const exportRoster = () => {
    window.open(`${API}/admin/market-camp-groups/export?payment_status=${paymentFilter}`, "_blank");
  };

  const toggleCheckIn = async (ticket_code, current) => {
    try {
      await axios.patch(`${API}/admin/market-camp-tickets/${ticket_code}/check-in`, { checked_in: !current });
      toast.success(`${ticket_code} ${!current ? "checked in" : "un-checked"}`);
      refresh();
    } catch { toast.error("Failed to update check-in"); }
  };

  const resendEmail = async (registration_id) => {
    try {
      await axios.post(`${API}/admin/market-camp-registrations/${registration_id}/resend-email`);
      toast.success("Email re-sent to parent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to resend"); }
  };

  const resendWhatsApp = async (registration_id) => {
    if (whatsappConfigured) {
      try {
        await axios.post(`${API}/admin/market-camp-registrations/${registration_id}/resend-whatsapp`);
        toast.success("WhatsApp sent to parent");
        return;
      } catch (err) {
        toast.error(err?.response?.data?.detail || "WhatsApp failed — opening manual chat");
      }
    }
    try {
      const r = await axios.get(`${API}/admin/market-camp-registrations/${registration_id}/whatsapp-link`);
      if (!r.data.parent_whatsapp_url) { toast.error("No parent phone on file."); return; }
      window.open(r.data.parent_whatsapp_url, "_blank");
    } catch { toast.error("Failed to open WhatsApp"); }
  };

  const printRoster = () => {
    const grp = groups?.[activeTab];
    if (!grp || grp.count === 0) { toast.error("No children to print for this group."); return; }
    const meta = GROUP_META[activeTab];
    const win = window.open("", "_blank");
    if (!win) { toast.error("Pop-up blocked. Allow pop-ups to print."); return; }
    const rows = filteredChildren.map((c, i) => `
      <tr>
        <td>${i + 1}</td>
        <td><strong>${c.full_name}</strong><br><span class="muted">Age ${c.age} · ${c.ticket_code}</span></td>
        <td>${c.parent_full_name}<br><span class="muted">${c.parent_phone}</span></td>
        <td>${c.emergency_contact_name || "—"}<br><span class="muted">${c.emergency_contact_phone || "—"}</span></td>
        <td class="al">${hasFlag(c.allergies) ? `<strong class="red">${c.allergies}</strong>` : "—"}</td>
        <td>${hasFlag(c.dietary_restrictions) ? c.dietary_restrictions : "—"}</td>
        <td>${hasFlag(c.medical_conditions) ? c.medical_conditions : "—"}</td>
        <td>${hasFlag(c.medications) ? c.medications : "—"}</td>
        <td>${c.authorized_pickup || "—"}</td>
        <td><div class="box"></div></td>
      </tr>`).join("");
    win.document.write(`<!DOCTYPE html><html><head><title>${meta.title} · Roster</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 16px; }
        h1 { color: ${meta.color}; margin: 0 0 4px; font-size: 22px; }
        .sub { color: #555; font-size: 12px; margin-bottom: 14px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { background: ${meta.color}; color: white; padding: 6px; text-align: left; }
        td { padding: 6px; border-bottom: 1px solid #ddd; vertical-align: top; }
        .muted { color: #777; font-size: 10px; }
        .red { color: #c00; }
        .al { background: #fff5f5; }
        .box { width: 18px; height: 18px; border: 2px solid #333; display: inline-block; }
        .footer { margin-top: 18px; font-size: 10px; color: #777; }
      </style></head><body>
      <h1>${meta.title} · ${meta.label}</h1>
      <p class="sub">Mini Makers Market Camp · Daily Roster · ${grp.count} children</p>
      <table>
        <thead><tr>
          <th>#</th><th>Child</th><th>Parent</th><th>Emergency</th>
          <th>Allergies</th><th>Dietary</th><th>Medical</th><th>Meds</th>
          <th>Authorized Pickup</th><th>Present?</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="footer">Printed ${new Date().toLocaleString()} · Mix & Mingle Events</p>
      <script>window.onload = () => { setTimeout(() => window.print(), 250); };</script>
      </body></html>`);
    win.document.close();
  };

  if (loading || !groups) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>
      </AdminLayout>
    );
  }

  const currentGroup = groups[activeTab];
  const meta = GROUP_META[activeTab];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-market-camp-groups-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-1">
              Daily Group Roster
            </h1>
            <p className="text-[#A1A1AA]">All paid campers grouped by age — your one-page reference for daily camp management.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/market-camp-registrations" className="px-3 py-2 rounded-lg border border-[rgba(212,175,55,0.3)] text-[#D4AF37] text-sm font-bold inline-flex items-center gap-1.5">
              <Users className="w-4 h-4" /> All Bookings
            </Link>
            <Link to="/admin/market-camp-tickets" className="px-3 py-2 rounded-lg border border-[rgba(212,175,55,0.3)] text-[#D4AF37] text-sm font-bold">Tickets</Link>
            <button onClick={refresh} className="px-3 py-2 rounded-lg border border-[rgba(212,175,55,0.3)] text-[#D4AF37] text-sm font-bold inline-flex items-center gap-1.5" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={printRoster} className="px-3 py-2 rounded-lg bg-[#5B2A86] text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="print-roster-btn">
              <Printer className="w-4 h-4" /> Print {meta.title}
            </button>
            <button onClick={exportRoster} className="px-3 py-2 rounded-lg bg-[#D4AF37] text-[#050505] text-sm font-bold inline-flex items-center gap-1.5" data-testid="export-roster-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total Children" value={totals.children} icon={Users} accent="#D4AF37" />
          <StatCard label="With Allergies" value={totals.allergy} icon={AlertTriangle} accent="#EF4444" />
          <StatCard label="With Dietary" value={totals.dietary} icon={Utensils} accent="#F2762E" />
          <StatCard label="Medical Conditions" value={totals.medical} icon={Heart} accent="#E91E80" />
          <StatCard label="Checked In" value={`${totals.checkedIn}/${totals.children}`} icon={CheckCircle2} accent="#22C55E" />
        </div>

        {/* Group Tabs (color-coded) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {GROUP_ORDER.map(key => {
            const g = groups[key];
            const m = GROUP_META[key];
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`rounded-2xl p-4 text-left border-2 transition-all ${isActive ? "shadow-lg scale-[1.02]" : "hover:scale-[1.01]"}`}
                style={{
                  borderColor: isActive ? m.color : m.border,
                  backgroundColor: isActive ? m.bg : "rgba(20,20,20,0.6)",
                }}
                data-testid={`group-tab-${key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase font-extrabold tracking-wider text-white" style={{ backgroundColor: m.color }}>
                    {m.label}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#A1A1AA]" />
                </div>
                <h3 className="text-lg font-black mb-1" style={{ color: m.color }}>{m.title}</h3>
                <p className="text-3xl font-black text-[#F5F5F0]" data-testid={`group-count-${key}`}>{g.count}</p>
                <p className="text-xs text-[#A1A1AA]">{g.count === 1 ? "child" : "children"}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {g.allergy_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 font-bold">⚠ {g.allergy_count} allergy</span>}
                  {g.medical_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-500/20 text-pink-300 font-bold">♥ {g.medical_count} medical</span>}
                  {g.checked_in_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 font-bold">✓ {g.checked_in_count} in</span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Active group section */}
        <div className="rounded-2xl border-2 p-4 sm:p-6" style={{ borderColor: meta.border, backgroundColor: meta.bg }}>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-black" style={{ color: meta.color }}>
                <Sparkles className="inline w-5 h-5 mr-1.5 -mt-1" /> {meta.title} <span className="text-base font-bold text-[#A1A1AA]">· {meta.label}</span>
              </h2>
              <p className="text-sm text-[#A1A1AA]">{currentGroup.count} {currentGroup.count === 1 ? "child" : "children"} registered</p>
            </div>
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <input
                type="text"
                placeholder="Search child / parent / ticket…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                data-testid="group-search"
              />
            </div>
          </div>

          {filteredChildren.length === 0 ? (
            <div className="text-center py-12 text-[#A1A1AA]">
              {currentGroup.count === 0 ? "No children registered in this group yet." : "No matches for your search."}
            </div>
          ) : (
            <div className="space-y-3" data-testid="group-children-list">
              {filteredChildren.map((c) => (
                <ChildCard
                  key={c.ticket_code}
                  child={c}
                  groupColor={meta.color}
                  whatsappConfigured={whatsappConfigured}
                  onCheckIn={() => toggleCheckIn(c.ticket_code, c.checked_in)}
                  onEmail={() => resendEmail(c.registration_id)}
                  onWhatsApp={() => resendWhatsApp(c.registration_id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Payment filter */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[#A1A1AA]">Showing:</span>
          {["paid", "all"].map(v => (
            <button
              key={v}
              onClick={() => setPaymentFilter(v)}
              className={`px-3 py-1 rounded text-xs font-bold uppercase ${paymentFilter === v ? "bg-[#D4AF37] text-[#050505]" : "border border-[rgba(212,175,55,0.3)] text-[#A1A1AA]"}`}
              data-testid={`payment-filter-${v}`}
            >
              {v === "paid" ? "Paid only" : "All registrations"}
            </button>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[rgba(212,175,55,0.1)]">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon className="w-4 h-4" style={{ color: accent }} />
      <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">{label}</span>
    </div>
    <p className="text-2xl font-black" style={{ color: accent }} data-testid={`total-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>{value}</p>
  </div>
);

const ChildCard = ({ child, groupColor, whatsappConfigured, onCheckIn, onEmail, onWhatsApp }) => {
  const allergyFlag = hasFlag(child.allergies);
  const dietaryFlag = hasFlag(child.dietary_restrictions);
  const medicalFlag = hasFlag(child.medical_conditions);
  const medFlag = hasFlag(child.medications);
  const supportFlag = hasFlag(child.support_needs);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl p-4 bg-[#0F0F0F] border-2"
      style={{ borderColor: "rgba(212,175,55,0.15)" }}
      data-testid={`child-card-${child.ticket_code}`}
    >
      <div className="grid lg:grid-cols-[280px_1fr_auto] gap-4">
        {/* Left: child + ticket */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase font-extrabold tracking-wider text-white px-2 py-0.5 rounded" style={{ backgroundColor: groupColor }}>
              Age {child.age}
            </span>
            {child.photo_consent && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#22C55E]"><Camera className="w-3 h-3" /> photo OK</span>
            )}
          </div>
          <h4 className="text-lg font-black text-[#F5F5F0]">{child.full_name}</h4>
          <p className="text-xs text-[#D4AF37] font-mono">Ticket {child.ticket_code} · Reg {child.registration_code}</p>
          {child.date_of_birth && <p className="text-xs text-[#52525B] mt-0.5">DOB: {child.date_of_birth}</p>}
          <div className="mt-2">
            <button
              onClick={onCheckIn}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold ${child.checked_in ? "bg-green-500 text-white" : "bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.3)] text-[#A1A1AA]"}`}
              data-testid={`checkin-${child.ticket_code}`}
            >
              {child.checked_in ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {child.checked_in ? "Checked in" : "Check in"}
            </button>
          </div>
        </div>

        {/* Middle: medical + parent details */}
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <DataBlock title="Parent" color="#D4AF37">
            <p className="text-[#F5F5F0] font-medium">{child.parent_full_name}</p>
            <p className="text-[#A1A1AA]"><Phone className="inline w-3 h-3 mr-1" />{child.parent_phone || "—"}</p>
            <p className="text-[#A1A1AA] truncate">{child.parent_email || "—"}</p>
          </DataBlock>

          <DataBlock title="Emergency contacts" color="#F2762E">
            <p className="text-[#F5F5F0]">{child.emergency_contact_name || "—"}</p>
            <p className="text-[#A1A1AA]">{child.emergency_contact_phone || "—"}</p>
            {(child.secondary_emergency_name || child.secondary_emergency_phone) && (
              <>
                <p className="text-[#F5F5F0] mt-1">{child.secondary_emergency_name || "—"}</p>
                <p className="text-[#A1A1AA]">{child.secondary_emergency_phone || "—"}</p>
              </>
            )}
          </DataBlock>

          <DataBlock title="Authorized pickup" color="#0FA9A0">
            <p className="text-[#F5F5F0]">{child.authorized_pickup || "—"}</p>
            <div className="mt-2 flex gap-1.5 flex-wrap" data-testid={`group-pickup-photos-${child.ticket_code}`}>
              {child.pickup_id_storage_path ? (
                <a
                  href={`${API}/admin/market-camp/pickup-photo/${child.registration_id}/id`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 text-[10px] font-bold uppercase tracking-wider hover:bg-green-900/60"
                  data-testid={`group-pickup-id-link-${child.ticket_code}`}
                >
                  <CheckCircle2 className="w-3 h-3" /> Photo ID
                </a>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-300 text-[10px] font-bold uppercase tracking-wider">
                  <AlertTriangle className="w-3 h-3" /> No ID
                </span>
              )}
              {child.pickup_selfie_storage_path ? (
                <a
                  href={`${API}/admin/market-camp/pickup-photo/${child.registration_id}/selfie`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 text-[10px] font-bold uppercase tracking-wider hover:bg-green-900/60"
                  data-testid={`group-pickup-selfie-link-${child.ticket_code}`}
                >
                  <CheckCircle2 className="w-3 h-3" /> Selfie
                </a>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-300 text-[10px] font-bold uppercase tracking-wider">
                  <AlertTriangle className="w-3 h-3" /> No Selfie
                </span>
              )}
            </div>
          </DataBlock>

          <DataBlock title="Health & safety" color={allergyFlag || medicalFlag ? "#EF4444" : "#52525B"} highlight={allergyFlag || medicalFlag}>
            <p>
              <span className="text-[#A1A1AA] font-bold">Allergies:</span>{" "}
              {allergyFlag ? <span className="text-red-400 font-bold">{child.allergies}</span> : <span className="text-[#52525B]">none</span>}
            </p>
            <p>
              <span className="text-[#A1A1AA] font-bold">Dietary:</span>{" "}
              {dietaryFlag ? <span className="text-orange-400">{child.dietary_restrictions}</span> : <span className="text-[#52525B]">none</span>}
            </p>
            <p>
              <span className="text-[#A1A1AA] font-bold">Medical:</span>{" "}
              {medicalFlag ? <span className="text-pink-400">{child.medical_conditions}</span> : <span className="text-[#52525B]">none</span>}
            </p>
            <p>
              <span className="text-[#A1A1AA] font-bold">Medications:</span>{" "}
              {medFlag ? <span className="text-[#F5F5F0]">{child.medications}</span> : <span className="text-[#52525B]">none</span>}
            </p>
            {supportFlag && (
              <p className="mt-1 pt-1 border-t border-[rgba(212,175,55,0.1)]">
                <span className="text-[#A1A1AA] font-bold">Support:</span>{" "}
                <span className="text-[#F5F5F0]">{child.support_needs}</span>
              </p>
            )}
          </DataBlock>
        </div>

        {/* Right: actions */}
        <div className="flex lg:flex-col gap-2 lg:w-[120px]">
          <button onClick={onEmail} className="flex-1 lg:w-full px-2.5 py-1.5 rounded bg-[rgba(212,175,55,0.1)] text-[#D4AF37] text-xs font-bold border border-[rgba(212,175,55,0.3)] inline-flex items-center justify-center gap-1.5" data-testid={`email-${child.ticket_code}`}>
            <Mail className="w-3.5 h-3.5" /> Email
          </button>
          <button onClick={onWhatsApp} className="flex-1 lg:w-full px-2.5 py-1.5 rounded bg-green-900/30 text-green-400 text-xs font-bold border border-green-500/30 inline-flex items-center justify-center gap-1.5" data-testid={`whatsapp-${child.ticket_code}`}>
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const DataBlock = ({ title, color, highlight, children }) => (
  <div
    className="rounded-lg p-2.5"
    style={{
      backgroundColor: highlight ? "rgba(239,68,68,0.06)" : "rgba(20,20,20,0.4)",
      border: `1px solid ${highlight ? "rgba(239,68,68,0.3)" : "rgba(212,175,55,0.1)"}`,
    }}
  >
    <p className="text-[10px] uppercase font-extrabold tracking-wider mb-1" style={{ color }}>{title}</p>
    {children}
  </div>
);

export default AdminMarketCampGroups;
